import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
  AlertDialogOverlay, Button, FormLabel, Input, Text, VStack,
} from '@chakra-ui/react';
import VoidDialogProvider from './VoidDialogProvider';

// dart (2026-07-14) — an in-app replacement for window.confirm. Native browser dialogs get
// popup-blocked (Brave especially) — once blocked, confirm() returns false and the guarded action
// silently fails, so users "can't save or progress" (Chad). This renders a Chakra AlertDialog the
// browser can't block and resolves a promise, so callers stay a one-liner: `if (!(await confirm(...))) return;`.
// item 842 (Chad) — also replaces window.prompt (the "azurewebsites.net says" native dialog) with a
// Basix-styled prompt: `const v = await prompt('Label', { title }); if (v === null) return;`.
export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;
type PromptFn = (message: string, options?: PromptOptions) => Promise<string | null>;

const ConfirmContext = createContext<ConfirmFn>(async () => true);
const PromptContext = createContext<PromptFn>(async () => null);

export const useConfirm = (): ConfirmFn => useContext(ConfirmContext);
export const usePrompt = (): PromptFn => useContext(PromptContext);

interface DialogState {
  mode: 'confirm' | 'prompt';
  message: string;
  options: PromptOptions;
  resolve: (value: boolean | string | null) => void;
}

const ConfirmProvider: React.FC = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [value, setValue] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const confirm = useCallback<ConfirmFn>((message, options = {}) => new Promise<boolean>((resolve) => {
    setDialog({
      mode: 'confirm', message, options, resolve: resolve as (v: boolean | string | null) => void,
    });
  }), []);

  const prompt = useCallback<PromptFn>((message, options = {}) => new Promise<string | null>((resolve) => {
    setValue(options.defaultValue ?? '');
    setDialog({
      mode: 'prompt', message, options, resolve: resolve as (v: boolean | string | null) => void,
    });
  }), []);

  // focus the input when a prompt opens
  useEffect(() => {
    if (dialog?.mode === 'prompt') {
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [dialog]);

  const isPrompt = dialog?.mode === 'prompt';

  const close = (ok: boolean) => {
    if (!dialog) return;
    if (dialog.mode === 'prompt') dialog.resolve(ok ? value : null);
    else dialog.resolve(ok);
    setDialog(null);
    setValue('');
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      <PromptContext.Provider value={prompt}>
        {/* EPIC_VOID_FLOW_2026-10 — the shared void dialog rides every shell that has confirm */}
        <VoidDialogProvider>{children}</VoidDialogProvider>
        <AlertDialog
          isOpen={!!dialog}
          leastDestructiveRef={cancelRef}
          onClose={() => close(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {dialog?.options.title ?? (isPrompt ? 'Enter a value' : 'Please confirm')}
              </AlertDialogHeader>
              <AlertDialogBody>
                {isPrompt ? (
                  <VStack align="stretch" spacing={2}>
                    {dialog?.message && (
                      <FormLabel m={0} fontSize="sm" fontWeight="medium" whiteSpace="pre-line">
                        {dialog.message}
                      </FormLabel>
                    )}
                    <Input
                      ref={inputRef}
                      value={value}
                      placeholder={dialog?.options.placeholder}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') close(true); }}
                    />
                  </VStack>
                ) : (
                  <Text whiteSpace="pre-line">{dialog?.message}</Text>
                )}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} variant="ghost" onClick={() => close(false)}>
                  {dialog?.options.cancelLabel ?? 'Cancel'}
                </Button>
                <Button
                  colorScheme={dialog?.options.destructive ? 'red' : 'brandGreen'}
                  ml={3}
                  onClick={() => close(true)}
                >
                  {dialog?.options.confirmLabel ?? (isPrompt ? 'OK' : 'Confirm')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
